import {cancelledUpload} from '../../utils/completionState';
import RouteMenu from '../../components/RouteMenu';
import { composeAddress } from '../../utils/address';
import { surveyorSupplementPath } from '../../utils/surveyorSupplementPath';
import { uploadResumePath } from '../../utils/uploadRecovery';
import UnreadChatBadge from '../../components/UnreadChatBadge';
import { arrivalReady } from '../../dispatchUi';
import SurveyorAdditionalActions from '../../components/Quality/SurveyorAdditionalActions';
import { hasAdditional } from '../../components/Quality/additionalActionsUi';
import { Box, Button, Chip, Stack, Typography } from "@mui/joy";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";

import CalendarMonthRoundedIcon     from '@mui/icons-material/CalendarMonthRounded';
import LocationOnRoundedIcon        from '@mui/icons-material/LocationOnRounded';
import AccessTimeRoundedIcon        from '@mui/icons-material/AccessTimeRounded';
import SquareFootRoundedIcon        from '@mui/icons-material/SquareFootRounded';
import PhoneRoundedIcon             from '@mui/icons-material/PhoneRounded';
import OpenInNewRoundedIcon         from '@mui/icons-material/OpenInNewRounded';
import WarningAmberRoundedIcon      from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon       from '@mui/icons-material/CheckCircleRounded';
import HourglassEmptyRoundedIcon    from '@mui/icons-material/HourglassEmptyRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import DrawRoundedIcon              from '@mui/icons-material/DrawRounded';
import TaskAltRoundedIcon           from '@mui/icons-material/TaskAltRounded';
import CancelRoundedIcon            from '@mui/icons-material/CancelRounded';
import ChatRoundedIcon              from '@mui/icons-material/ChatRounded';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';

import { AmountAgreementControl } from "../../components/AmountAgreement";
import PaymentBreakdown from "../../components/Payments/PaymentBreakdown";




// ─── Константы ────────────────────────────────────────────────────────────────

const MONTHS_SHORT = ["янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек"];

const STATE_CONFIG = {
    completed: { label: 'Завершено', color: '#10b981', bg: 'rgba(16,185,129,0.1)',  icon: <CheckCircleRoundedIcon sx={{ fontSize: 15 }} /> },
    in_progress: { label: 'В работе', color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)',  icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 15 }} /> },
    new: { label: 'Новое', color: '#6366f1', bg: 'rgba(99,102,241,0.1)',  icon: null },
    measuring_premises: { label: 'Запланировано', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  icon: <CalendarMonthRoundedIcon sx={{ fontSize: 15 }} /> },
    drawing: { label: 'На черчении', color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)',  icon: <DrawRoundedIcon sx={{ fontSize: 15 }} /> },
    cancelled: { label: 'Отменена', color: '#e91d0e', bg: 'rgba(233, 29, 14, 0.1)',  icon: <HighlightOffRoundedIcon sx={{ fontSize: 15 }} /> },
    moved: { label: 'Перенесена', color: '#cd7b00', bg: 'rgba(205, 123, 0, 0.1)', icon: <HighlightOffRoundedIcon sx={{ fontSize: 15 }} /> },
    postponed_to_date: { label: 'Перенесена на дату', color: '#cd7b00', bg: 'rgba(205, 123, 0, 0.1)', icon: <HighlightOffRoundedIcon sx={{ fontSize: 15 }} /> },
    additional_measuring: { label: 'Доп. обмер', color: '#cd7b00', bg: 'rgba(205, 123, 0, 0.1)', icon: <HighlightOffRoundedIcon sx={{ fontSize: 15 }} /> },
    uploading: { label: 'Загрузка файлов', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: <CloudUploadRoundedIcon sx={{ fontSize: 15 }} /> },
};

// ─── Хелперы ──────────────────────────────────────────────────────────────────

function formatTime(t) { return t ? String(t).slice(0, 5) : null; }

function buildTimeString(item) {
    const from = formatTime(item?.time_from);
    const to   = formatTime(item?.time_to);
    const main = formatTime(item?.time_event);
    if (from && to) return `${from} — ${to}`;
    if (main) return main;
    return null;
}

function isOverdue(item) {
    if (item?.state === 'completed' || item?.is_completed) return false;
    if (!item?.date_event) return false;
    const raw = item.date_event;
    let d;
    if (raw.includes('.')) {
        const p = raw.split('.');
        d = new Date(`${p[2]}-${p[1]}-${p[0]}`);
    } else {
        d = new Date(raw);
    }
    return d < new Date(new Date().toDateString());
}

function parseDateParts(dateStr) {
    if (!dateStr) return { day: '—', monthIdx: 0 };
    const raw = dateStr;
    if (raw.includes('.')) {
        const p = raw.split('.');
        return { day: p[0], monthIdx: Number(p[1]) - 1, year: p[2] };
    } else {
        const p = raw.split('-');
        return { day: p[2], monthIdx: Number(p[1]) - 1, year: p[0] };
    }
}

// ─── SurveyorEventCard ────────────────────────────────────────────────────────
//
// Props:
//   item        — объект события (обязательно)
//   variant     — 'upcoming' | 'active' | 'full'
//                   upcoming — дашборд, предстоящие (синяя тема, только кнопка "Перейти")
//                   active   — дашборд, нужно закрыть (красно-жёлтая тема, только кнопка "Перейти")
//                   full     — страница "Мои события" (все кнопки: Завершить, Неудачная, Чат, Дополнить)
//   onOpen      — () => void — открыть модалку с деталями (все варианты)
//   onComplete  — () => void — завершить событие (только full)
//   onFail      — () => void — неудачная (только full)
//   onChat      — () => void — открыть чат (только full)
//   onSupplement— () => void — дополнить обмер (только full)
//   animate     — bool, default true — обёртка motion.div (можно отключить для списков без framer)

export default function SurveyorEventCard({
    item, 
    showAmountAgreement = false, 
    showBreakdown = false,
    onRefresh,
    variant = 'upcoming',
    onOpen,
    onComplete,
    onFail,
    onChat,
    onSupplement,
    animate = true,
    uploadProcesses = [],
}) {
    const deal = item?.bitrix_deal;
    const city = deal?.city?.name;
    const address = deal?.address;
    const full = composeAddress(city, address) || '—';
    const timeStr = buildTimeString(item);
    const overdue = isOverdue(item);
    const isDone = item?.state === 'completed' || item?.is_completed;
    const canSupplement = !hasAdditional(item) && item?.is_completed && ['drawing', 'completed'].includes(item?.state);
    const hasChat = Boolean(item?.event_chat?.id);

    const navigate = useNavigate();

    // Задача 4: открыть событие (страница обмерщика) — по клику на номер и на карточку.
    const openEvent = () => {
        const bid = item?.bitrix_deal?.id;
        if (bid) navigate(`/surveyor/event/${bid}`);
    };

    const activeCycleId = item?.additional_measurement?.status === 'active' ? item.additional_measurement.id : null;
    const remoteProcess = item?.completion_process;
    const matchingCycle = process => String(process?.additional_measurement_id ?? '') === String(activeCycleId ?? '');
    const processRows = new Map(uploadProcesses.filter(p => String(p.event_id) === String(item?.id)).map(p => [String(p.process_id), p]));
    if (remoteProcess) processRows.set(String(remoteProcess.process_id), { ...processRows.get(String(remoteProcess.process_id)), ...remoteProcess });
    const process = [...processRows.values()].filter(p => !cancelledUpload(p) && matchingCycle(p)
        && ['uploading', 'pending', 'processing', 'failed'].includes(p.status)).sort((a, b) => {
            const live = p => ['uploading', 'pending', 'processing'].includes(p.status) ? 1 : 0;
            return live(b) - live(a) || Number(b.process_id) - Number(a.process_id);
        })[0] || null;
    const isUploading = Boolean(process && ['uploading', 'pending', 'processing'].includes(process.status));
    const completionFailed = process?.status === 'failed';
    const processIsSupplement = Boolean(activeCycleId) || process?.process_type === 'supplement' || process?.type === 'supplement';
    const openProcess = () => {
        const confirmedPath = uploadResumePath(process?.resume_url, item?.id);
        if (confirmedPath) { navigate(confirmedPath); return; }
        const query = new URLSearchParams({ event: String(item.id), process: String(process.process_id) });
        if (activeCycleId) query.set('additional', String(activeCycleId));
        navigate(`/surveyor/${processIsSupplement ? 'supplement' : 'close'}/${item?.bitrix_deal?.id}?${query}`);
    };
    
    // Статус берём готовым с сервера: effective_state уже учитывает анкету
    // (отмена, перенос), допобмер и стадию события. Раньше фронт разбирал
    // это сам, и любое изменение правил на сервере надо было дублировать здесь.
    const effectiveState = item?.effective_state || item?.state;
    const isCancelledLike = ['cancelled', 'moved', 'postponed_to_date'].includes(effectiveState);

    const cfg = completionFailed ? { label: processIsSupplement ? 'Ошибка дополнения' : 'Завершение не подтверждено', color: '#c30013', bg: 'rgba(195,0,19,0.08)', icon: <WarningAmberRoundedIcon sx={{ fontSize: 15 }} /> } : isUploading
        ? { ...STATE_CONFIG.uploading, label: process.status === 'uploading' ? 'Передача не завершена' : (processIsSupplement ? 'Обработка дополнения' : 'Завершаем обмер') }
        : hasAdditional(item) ? STATE_CONFIG.additional_measuring : (STATE_CONFIG[effectiveState] || STATE_CONFIG[item?.state] || STATE_CONFIG['new']);
    const { day, monthIdx, year } = parseDateParts(item?.date_event);

    // ── Цветовая тема по варианту + overdue ───────────────────────────────────
    let accentColor, accentBg, topGradient, borderColor;

    // Страница событий — тема зависит от состояния
    if (overdue && !isCancelledLike) {
        accentColor = '#c30013'; accentBg = 'rgba(195,0,19,0.08)';
        topGradient = 'linear-gradient(90deg, #c30013, #e05060)';
        borderColor = 'rgba(195,0,19,0.22)';
    } else if (effectiveState === 'cancelled') {
        accentColor = '#c30013'; accentBg = 'rgba(195,0,19,0.08)';
        topGradient = 'linear-gradient(90deg, #c30013, #e05060)';
        borderColor = 'rgba(195,0,19,0.22)';
    } else if (effectiveState === 'moved' || effectiveState === 'postponed_to_date') {
        // Перенос — не ошибка, поэтому не красный
        accentColor = '#cd7b00'; accentBg = 'rgba(205,123,0,0.08)';
        topGradient = 'linear-gradient(90deg, #cd7b00, #e0a050)';
        borderColor = 'rgba(205,123,0,0.22)';
    } else if (isDone) {
        accentColor = '#1F7A1F'; accentBg = 'rgba(31,122,31,0.08)';
        topGradient = 'linear-gradient(90deg, #1F7A1F, #51BC51)';
        borderColor = 'rgba(31,122,31,0.18)';
    } else {
        accentColor = '#572AC3'; accentBg = 'rgba(87,42,195,0.08)';
        topGradient = `linear-gradient(90deg, ${cfg.color}, ${cfg.color}99)`;
        borderColor = 'rgba(87,42,195,0.11)';
    }


    // ── Карточка ──────────────────────────────────────────────────────────────
    const card = (
        <Box
            onClick={openEvent}
            sx={{
            borderRadius: '16px',
            border: `1.5px solid ${hasAdditional(item) ? '#f59e0b' : borderColor}`,
            background: overdue && variant !== 'full' ? 'rgba(239,68,68,0.02)' : 'white',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            height: variant === 'full' ? '100%' : 'auto',
            transition: 'box-shadow 0.18s, transform 0.18s',
            '&:hover': {
                boxShadow: `0 6px 24px ${accentColor}22`,
                transform: 'translateY(-1px)',
            },
        }}>
            {/* ── Цветная полоска ── */}
            <Box sx={{ height: variant === 'full' ? 4 : 3, background: topGradient, flexShrink: 0 }} />

            <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <SurveyorAdditionalActions event={item} onChanged={onRefresh} />
                <Stack direction="column" spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>

                    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
                        {/* ── Дата-плашка ── */}
                        <Stack direction="column" spacing={1} alignItems="flex-start" sx={{ flexShrink: 0, maxWidth: '45%' }}>
                            <Box sx={{
                                minWidth: 65,
                                height: 65,
                                borderRadius: '13px',
                                background: cfg.bg,
                                border: `1.5px solid ${cfg.color}`,
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Typography component={'p'} level="h3" sx={{
                                    fontWeight: 800, color: cfg.color, lineHeight: 1,
                                    fontFamily: "'Sora', sans-serif",
                                }}>
                                    {day}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '0.65rem', color: cfg.color, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.85,
                                }}>
                                    {MONTHS_SHORT[monthIdx]}
                                </Typography>
                                {year && <Typography sx={{ fontSize: '0.6rem', color: cfg.color, lineHeight: 1, mt: 0.25 }}>{year}</Typography>}
                            </Box>

                            {/* Время под плашкой (только upcoming) */}
                            {timeStr && (
                                <Chip color="neutral" variant="soft" size="md">
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <AccessTimeRoundedIcon sx={{ fontSize: 13, color: 'neutral.600' }} />
                                        <Typography level="body-sm" sx={{ color: 'neutral.600', fontWeight: 500 }}>
                                            {timeStr}
                                        </Typography>
                                    </Stack>
                                </Chip>
                            )}
                        </Stack>
                        
                        {/* ── Основная информация ── */}
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>

                            {/* Заголовок + статус */}
                            <Stack direction={{ xs: "column", sm: "row" }} sx={{ gap: 0.5 }} spacing={0} alignItems="flex-start" justifyContent="space-between">
                                <Typography
                                    level="body-lg"
                                    onClick={(e) => { e.stopPropagation(); openEvent(); }}
                                    endDecorator={<OpenInNewRoundedIcon sx={{ fontSize: 15 }} />}
                                    sx={{
                                        fontWeight: 700, fontFamily: "'Sora', sans-serif",
                                        color: accentColor, cursor: 'pointer',
                                        textDecorationLine: 'underline', textDecorationStyle: 'dotted',
                                        textUnderlineOffset: '3px',
                                        '&:hover': { color: '#572AC3' },
                                    }}
                                    noWrap
                                >
                                    №{deal?.id}
                                </Typography>
                                <Stack direction="row" sx={{ flexShrink: 0, gap: 0.5 }}>
                                    {overdue && (
                                        <Chip size="sm" sx={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontWeight: 700, border: 'none', fontSize: '0.63rem' }}
                                            startDecorator={<WarningAmberRoundedIcon sx={{ fontSize: 11 }} />}
                                        >
                                            Просрочено
                                        </Chip>
                                    )}
                                    <Chip size="md" sx={{ background: cfg.bg, color: cfg.color, fontWeight: 700, border: 'none', fontSize: '0.70rem' }}
                                        startDecorator={cfg.icon}
                                    >
                                        {cfg.label}
                                    </Chip>
                                </Stack>
                            </Stack>

                            {isCancelledLike && item?.questionnaire && (
                                <Box sx={{ mt: 0.75 }}>
                                    {item.questionnaire.reason_for_cancelation && (
                                        <Typography level="body-xs" sx={{ color: '#c30013' }}>
                                            Причина: {item.questionnaire.reason_for_cancelation}
                                        </Typography>
                                    )}
                                    {item.questionnaire.transfer_reason && (
                                        <Typography level="body-xs" sx={{ color: '#cd7b00' }}>
                                            Причина переноса: {item.questionnaire.transfer_reason}
                                        </Typography>
                                    )}
                                    {item.questionnaire.transfer_date_replacement && (
                                        <Typography level="body-xs" sx={{ color: '#cd7b00' }}>
                                            Перенесено на {new Date(item.questionnaire.transfer_date_replacement).toLocaleDateString('ru-RU')}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* Метаданные */}
                            <Stack direction="row" flexWrap="wrap" gap={1.2} sx={{ mt: 0.3 }}>
                                {deal?.square > 0 && (
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <SquareFootRoundedIcon sx={{ fontSize: 14, color: 'text.tertiary' }} />
                                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>{deal.square} м²</Typography>
                                    </Stack>
                                )}
                                {deal?.contact_phone && (
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <PhoneRoundedIcon sx={{ fontSize: 14, color: 'text.tertiary' }} />
                                        <Typography component="a" href={`tel:${String(deal.contact_phone).replace(/[^+\d]/g, '')}`} onClick={e => e.stopPropagation()} level="body-sm" sx={{ color: 'primary.700', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '3px', py: 0.4 }}>{deal.contact_phone}</Typography>
                                    </Stack>
                                )}
                            </Stack>
                        </Box>
                    </Stack>

                    {/* ── Основная информация ── */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>

                        {/* Адрес */}
                        <Stack direction="row" alignItems="center" spacing={0.7}>
                            <LocationOnRoundedIcon sx={{ fontSize: 14, color: accentColor, flexShrink: 0, mt: 0.15 }} />
                            <Typography level="body-sm" sx={{ color: '#1a1a2e', fontWeight: 500, lineHeight: 1.4 }}>
                                {full}
                            </Typography>
                        </Stack>

                        {process && <Box onClick={event => event.stopPropagation()} sx={{ mt: 1, p: 1.2, borderRadius: '12px', background: completionFailed ? 'rgba(195,0,19,0.05)' : 'rgba(87,42,195,0.05)' }}>
                            <Typography level="body-xs" color={completionFailed ? 'danger' : 'neutral'} sx={{ mb: 1 }}>
                                {completionFailed ? (process.retry_reason || process.error_message || 'Файлы сохранены, но завершение не подтверждено. Откройте загрузку, чтобы повторить обработку.') : process.status === 'uploading' ? 'Передача ещё не завершена. Если исходная вкладка закрыта, продолжите ту же загрузку.' : 'Файлы приняты. Сервер завершает обработку; повторно передавать их не нужно.'}
                            </Typography>
                            <Button size="sm" variant="soft" color={completionFailed ? 'danger' : 'primary'} onClick={openProcess} sx={{ borderRadius: '9px' }}>
                                {completionFailed ? 'Проверить завершение' : process.status === 'uploading' ? 'Продолжить загрузку' : 'Статус завершения'}
                            </Button>
                        </Box>}
                        {/* ── Кнопки ── */}
                        <Box sx={{ mt: 'auto', pt: 1 }} onClick={(e) => e.stopPropagation()}>
                            <Stack spacing={0.7}>
                                {!process && !hasAdditional(item) && !isDone && !isCancelledLike && ['measuring_premises', 'additional_measuring'].includes(item?.state) && (
                                    <Stack direction="row" spacing={0.7}>
                                        {arrivalReady(item) && (
                                        <Button size="sm" fullWidth
                                            startDecorator={<TaskAltRoundedIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => onComplete?.(item)}
                                            disabled={isUploading || !arrivalReady(item)}
                                            sx={{
                                                borderRadius: '10px', fontWeight: 700, 
                                                background: isUploading ? undefined : 'linear-gradient(135deg, #1F7A1F, #51BC51)',
                                                color: 'white', flex: 1, py: 0.7,
                                            }}
                                        >
                                            Завершить
                                        </Button>
                                        )}
                                        <Button size="sm" variant="soft"
                                            startDecorator={<CancelRoundedIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => onFail?.(item)}
                                            disabled={isUploading}
                                            sx={{
                                                borderRadius: '10px', fontWeight: 700,
                                                background: 'rgba(195,0,19,0.08)', color: '#c30013', py: 0.7,
                                                '&:hover': { background: isUploading ? undefined : 'rgba(195,0,19,0.15)' },
                                            }}
                                        >
                                            Неудачная
                                        </Button>
                                    </Stack>
                                )}
                                {canSupplement && !process && (
                                    <Button size="sm" fullWidth variant="soft"
                                        startDecorator={<AddPhotoAlternateRoundedIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => navigate(surveyorSupplementPath(item))}
                                        sx={{
                                            borderRadius: '10px', fontWeight: 700,
                                            background: 'rgba(15,123,171,0.08)', color: '#0f7bab', py: 0.7,
                                            '&:hover': { background: 'rgba(15,123,171,0.15)' },
                                        }}
                                    >
                                        Дополнить обмер
                                    </Button>
                                )}
                                <Stack direction="row" spacing={0.7} sx={{ flexWrap: 'wrap', gap: 0.7 }}>
                                    <RouteMenu deal={deal} />
                                    {(isDone || isCancelledLike || !['measuring_premises', 'additional_measuring'].includes(item?.state)) ? (
                                        <Button size="sm" variant="soft" color="neutral" fullWidth
                                            startDecorator={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
                                            onClick={(e) => { e.stopPropagation(); openEvent(); }}
                                            sx={{ borderRadius: '10px', fontWeight: 700, flex: 1, py: 0.7 }}
                                        >
                                            Открыть событие
                                        </Button>
                                    ) : hasAdditional(item) ? null : item?.arrival?.skipped ? (<Typography level="body-xs">Прибытие пропущено менеджером</Typography>) : arrivalReady(item) ? (
                                        <Button size="sm" variant="soft" color="success" fullWidth disabled
                                            startDecorator={<TaskAltRoundedIcon sx={{ fontSize: 16 }} />}
                                            sx={{ borderRadius: '10px', fontWeight: 700, flex: 1, py: 0.7 }}
                                        >
                                            {item?.arrival?.skipped ? 'Прибытие пропущено' : 'Вы на месте'}
                                        </Button>
                                    ) : (
                                        <Button size="sm" fullWidth
                                            startDecorator={<LocationOnRoundedIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => navigate(`/surveyor/arrive/${item?.id}`)}
                                            sx={{
                                                borderRadius: '10px', fontWeight: 700, flex: 1, py: 0.7,
                                                background: 'linear-gradient(135deg, #572AC3, #7B4DE0)',
                                                color: 'white',
                                            }}
                                        >
                                            Я на месте
                                        </Button>
                                    )}
                                    {onChat && (
                                        <Button size="sm" variant="soft"
                                            startDecorator={<UnreadChatBadge chatId={item?.event_chat?.id}><ChatRoundedIcon sx={{ fontSize: 16 }} /></UnreadChatBadge>}
                                            onClick={() => onChat?.(item)}
                                            sx={{
                                                borderRadius: '10px', fontWeight: 700, py: 0.7,
                                                background: 'rgba(87,42,195,0.08)', color: '#572AC3',
                                                '&:hover': { background: 'rgba(87,42,195,0.15)' },
                                            }}
                                        >
                                            Чат
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                        </Box>

                        {(showBreakdown || showAmountAgreement) && !item?.surveyor_payment?.breakdown?.some(row => row.kind === 'transfer') && (
                            <Typography level="body-sm" sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                {Number(item.transfer_amount || 0) > 0 ? `Выезд включён в выплату: ${Number(item.transfer_amount).toLocaleString('ru-RU')} ₽`
                                  : item.transfer_route?.available || item.transfer_is_manual ? 'Выезд: без доплаты' : 'Выезд: сумма пока не указана'}
                            </Typography>
                        )}
                        {(showBreakdown || showAmountAgreement) && item.transfer_route?.estimated && item.transfer_route?.available && <Typography level="body-xs" color="neutral" sx={{ mt: 0.75 }}>Выезд из города {item.transfer_route.origin_city_name} · ≈ {item.transfer_km} км в одну сторону</Typography>}

                        {showAmountAgreement && item?.surveyor_payment && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(0,0,0,0.06)" }} onClick={(e) => e.stopPropagation()}>
                            <Typography level="body-sm" sx={{ fontWeight: 700, mb: 0.75 }}>
                                Сумма оплаты: {Number(
                                    item.surveyor_payment.total_payout
                                    ?? item.surveyor_payment.total_surveyor
                                    ?? 0
                                ).toLocaleString("ru-RU")} ₽
                            </Typography>

                            {/* Из чего сложилась сумма: база + допы + выезд + итог */}
                            {Array.isArray(item.surveyor_payment.breakdown)
                                && item.surveyor_payment.breakdown.length > 0 && (
                                <PaymentBreakdown rows={item.surveyor_payment.breakdown} compact />
                            )}

                            <AmountAgreementControl
                                eventId={item.id}
                                role="surveyor"
                                agreement={item.surveyor_payment.agreement}
                                expectedAmount={item.surveyor_payment.expected_amount}
                                onChanged={onRefresh}
                            />
                            </Box>
                        )}

                        {showBreakdown && !showAmountAgreement && item?.surveyor_payment
                            && Array.isArray(item.surveyor_payment.breakdown)
                            && item.surveyor_payment.breakdown.length > 0 && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                                <Typography level="body-sm" sx={{ fontWeight: 700, mb: 0.75 }}>
                                    Сумма оплаты: {Number(
                                        item.surveyor_payment.total_payout
                                        ?? item.surveyor_payment.total_surveyor
                                        ?? 0
                                    ).toLocaleString("ru-RU")} ₽
                                </Typography>
                                <PaymentBreakdown rows={item.surveyor_payment.breakdown} compact />
                            </Box>
                        )}
                    </Box>
                </Stack>
            </Box>
        </Box>
    );

    if (!animate) return card;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.22 }}
            style={{ height: '100%' }}
        >
            {card}
        </motion.div>
    );
}
